import { useState, useMemo, useEffect, useContext } from "react";
import { Flex, Typography, Spin, App, Button, Grid } from "antd";

import "maplibre-gl/dist/maplibre-gl.css";
import {
  Map,
  FullscreenControl,
  NavigationControl,
  Layer,
  Source,
} from "@vis.gl/react-maplibre";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SearchIcon from "@/icons/Search";
import RightPanelDrawer from "./components/RightPanelDrawer";
import MobileRightPanelDrawer from "./components/MobileRightPanelDrawer";

import { useFilters } from "./hooks/useFilters";
import { useDefinitionsOptions } from "./hooks/useDefinitionsOptions";
import { useMapObjects } from "./hooks/useMapObjects";
import { useMapInteractions } from "./hooks/useMapInteractions";
import { useDebounce } from "./hooks/useDebounce";
import { useRightPanel } from "./hooks/useRightPanel";
import { useMapBbox } from "./hooks/useMapBbox";

import GeoLayers from "./components/GeoLayers";
import ClusterLayers from "./components/ClusterLayers";

import { UserContext } from "@/context/UserContext";

import routes from "@/routes";

import { MAP_STYLES } from "@/constants/map";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const DEFAULT_VIEW_STATE = {
  longitude: 103.2,
  latitude: 63.8,
  zoom: 3,
};

const LOCAL_STORAGE_KEY = "mapViewState";

const MapView = () => {
  const { user } = useContext(UserContext);
  const { message } = App.useApp();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const storageKey = `${user.user_uuid}_${LOCAL_STORAGE_KEY}`;

  const [viewState, setViewState] = useState(() => {
    const savedViewState = localStorage.getItem(storageKey);
    return savedViewState ? JSON.parse(savedViewState) : DEFAULT_VIEW_STATE;
  });
  const [requestZoom, setRequestZoom] = useState(() =>
    Math.floor(viewState.zoom),
  );
  const [mapStyle, setMapStyle] = useState("voyager");
  const [isMobilePanelMinimized, setIsMobilePanelMinimized] = useState(false);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(viewState));
  }, [storageKey, viewState]);

  const panel = useRightPanel();
  const { debouncedBbox, updateBbox } = useMapBbox();

  const { filters, onChangeFilters, isReady } = useFilters();

  const selectedDefinitionUuids = useMemo(
    () =>
      Array.isArray(filters.definition_uuid) ? filters.definition_uuid : [],
    [filters.definition_uuid],
  );
  const searchInSelectedDefinitions =
    filters.search_in_selected_definitions !== false;

  const debouncedSearch = useDebounce(filters.search, 700);
  const debouncedDefinition = useDebounce(selectedDefinitionUuids, 400);
  const debouncedSearchInSelectedDefinitions = useDebounce(
    searchInSelectedDefinitions,
    400,
  );

  const hasSearch = Boolean(
    typeof debouncedSearch === "string"
      ? debouncedSearch.trim()
      : debouncedSearch,
  );

  const effectiveDefinitionUuids = useMemo(() => {
    if (hasSearch && !debouncedSearchInSelectedDefinitions) {
      return [];
    }

    return debouncedDefinition;
  }, [
    debouncedDefinition,
    debouncedSearchInSelectedDefinitions,
    hasSearch,
  ]);

  const debouncedFilters = useMemo(
    () => ({
      search: debouncedSearch,
      definition_uuid: effectiveDefinitionUuids,
      search_in_selected_definitions: debouncedSearchInSelectedDefinitions,
    }),
    [
      debouncedSearch,
      effectiveDefinitionUuids,
      debouncedSearchInSelectedDefinitions,
    ],
  );

  const hasSelectedDefinitions =
    Array.isArray(debouncedFilters.definition_uuid) &&
    debouncedFilters.definition_uuid.length > 0;
  const canRequestMapObjects =
    hasSelectedDefinitions ||
    (hasSearch && !debouncedSearchInSelectedDefinitions);

  const { data, clusters, mode, isDataLoading, objectsTypes, foundObjects } =
    useMapObjects(
      message,
      isReady && canRequestMapObjects ? debouncedFilters : null,
      debouncedBbox,
      requestZoom,
    );
  const definitionsOptions = useDefinitionsOptions(objectsTypes);

  const defaultDefinitionUuids = useMemo(
    () =>
      definitionsOptions
        .filter((definition) => definition.is_default_on_map)
        .map((definition) => definition.uuid),
    [definitionsOptions],
  );

  const isDefinitionRequired =
    isReady &&
    objectsTypes.length > 0 &&
    selectedDefinitionUuids.length === 0 &&
    defaultDefinitionUuids.length === 0 &&
    (!hasSearch || searchInSelectedDefinitions);

  const matchedObjects = useMemo(() => {
    return foundObjects ?? [];
  }, [foundObjects]);

  const getObjectH3Indexes = (item) => {
    const indexes = item?.object?.h3Indexes;

    return Array.isArray(indexes) ? indexes : [];
  };

  const getClustersByObjects = (items = []) => {
    const h3Indexes = new Set();

    items.forEach((item) => {
      getObjectH3Indexes(item).forEach((h3Index) => {
        h3Indexes.add(h3Index);
      });
    });

    return clusters.filter((cluster) => h3Indexes.has(cluster.h3Index));
  };

  const {
    onMapLoad,
    zoomToObjects,
    zoomToClusters,
    mapRef,
    objectPending,
    resultsPending,
    fetchObject,
  } = useMapInteractions({
    data,
    message,
    filters: debouncedFilters,
    bbox: debouncedBbox,
    panelWidth: isMobile || panel.isPanelMinimized
      ? 0
      : (panel.isObjectOpen ? 400 : 0) + (panel.isSearchOpen ? 200 : 0),
    setIsObjectOpen: panel.setIsObjectOpen,
    setIsSearchOpen: panel.setIsSearchOpen,
    setCurrentObject: panel.setCurrentObject,
    setMapClickResults: panel.setMapClickResults,
    setIsRightPanelOpen: panel.setIsRightPanelOpen,
    setIsPanelMinimized: panel.setIsPanelMinimized,
  });

  const handleSelectObject = (value) => {
    panel.setIsRightPanelOpen(true);
    panel.setIsSearchOpen(true);
    panel.setIsObjectOpen(true);

    fetchObject(value.definition.uuid, value.object.uuid);

    zoomToObjects([value], isMobile ? 0 : 600);
  };

  const handleSearch = (value) => {
    onChangeFilters({ search: value || null });
    panel.setMapClickResults(null);
  };

  const handleSearchInSelectedDefinitionsChange = (value) => {
    onChangeFilters({ search_in_selected_definitions: value });
    panel.setMapClickResults(null);
  };

  const handleApplySearch = (items) => {
    if (!isMobile) {
      panel.setIsPanelMinimized(true);
    }

    if (mode === "clusters") {
      const matchedClusters = getClustersByObjects(items);

      if (matchedClusters.length) {
        zoomToClusters(matchedClusters, 0);
        return;
      }
    }

    zoomToObjects(items, 0);
  };

  useEffect(() => {
    if (!objectsTypes.length) return;

    const optionUuids = new Set(
      definitionsOptions.map((definition) => definition.uuid),
    );
    const validSelectedUuids = selectedDefinitionUuids.filter((uuid) =>
      optionUuids.has(uuid),
    );

    if (validSelectedUuids.length !== selectedDefinitionUuids.length) {
      onChangeFilters({
        definition_uuid: validSelectedUuids.length
          ? validSelectedUuids
          : defaultDefinitionUuids,
      });
      return;
    }

    if (!selectedDefinitionUuids.length && defaultDefinitionUuids.length) {
      onChangeFilters({ definition_uuid: defaultDefinitionUuids });
    }
  }, [
    objectsTypes.length,
    definitionsOptions,
    selectedDefinitionUuids,
    defaultDefinitionUuids,
    onChangeFilters,
  ]);

  const handleDefinitionChange = (value) => {
    const nextDefinitionUuids = Array.isArray(value) ? value : [];

    if (nextDefinitionUuids.length) {
      onChangeFilters({ definition_uuid: nextDefinitionUuids });
      return;
    }

    if (defaultDefinitionUuids.length) {
      onChangeFilters({ definition_uuid: defaultDefinitionUuids });
      return;
    }

    onChangeFilters({ definition_uuid: [] });
  };

  const handleCancelMapClick = () => {
    panel.setMapClickResults(null);
  };

  return (
    <>
      <Flex vertical gap={25} className="h-100">
        <Flex vertical gap={10}>
          <Breadcrumbs
            items={[
              {
                title: "Главная",
                href: routes.dashboards,
              },

              {
                title: "Карта",
              },
            ]}
          />

          <Title level={2} className="no-margin">
            Карта
          </Title>
        </Flex>

        <Flex vertical gap={20} style={{ flex: 1, minHeight: 0 }}>
          {/* Карта */}
          <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
            {(!panel.isRightPanelOpen || panel.isPanelMinimized) && (
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  right: 50,
                  zIndex: 20,
                }}
              >
                <Button
                  icon={<SearchIcon />}
                  onClick={() => {
                    panel.setIsRightPanelOpen(true);
                    panel.setIsPanelMinimized(false);
                    panel.setIsSearchOpen(true);
                  }}
                  style={{ height: 29 }}
                >
                  Поиск
                </Button>
              </div>
            )}
            <Map
              ref={mapRef}
              initialViewState={viewState}
              style={{ width: "100%", height: "100%" }}
              mapStyle={MAP_STYLES[mapStyle]}
              attributionControl={false}
              interactive
              onMove={(evt) => setViewState(evt.viewState)}
              onMoveEnd={(evt) => {
                setViewState(evt.viewState);
                setRequestZoom(Math.floor(evt.viewState.zoom));
                updateBbox(evt.target);
              }}
              onLoad={(evt) => {
                updateBbox(evt.target);
                onMapLoad(evt);
              }}
              preserveDrawingBuffer
            >
              <FullscreenControl />
              <NavigationControl showCompass={false} />
              <div
                style={{
                  position: "absolute",
                  bottom: isMobile && isMobilePanelMinimized ? 80 : 24,
                  left: 10,
                  zIndex: 20,
                }}
              >
                <Button
                  onClick={() =>
                    setMapStyle((prev) =>
                      prev === "voyager" ? "satellite" : "voyager",
                    )
                  }
                  style={{ height: 29 }}
                >
                  {mapStyle === "voyager" ? "Спутник" : "Схема"}
                </Button>
              </div>

              <Source
                id="highlighted-anchor-source"
                type="geojson"
                data={{
                  type: "FeatureCollection",
                  features: [],
                }}
              >
                <Layer
                  id="highlighted-anchor"
                  type="symbol"
                  layout={{
                    "icon-size": 0,
                  }}
                />
              </Source>

              <GeoLayers
                objects={mode === "objects" ? data : []}
                mapStyle={mapStyle}
              />

              <ClusterLayers
                clusters={mode === "clusters" ? clusters : []}
              />
            </Map>

            {isDataLoading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  // background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  pointerEvents: "none",
                  // backdropFilter: "blur(2px)",
                }}
              >
                <Spin size="large" />
              </div>
            )}
          </div>
        </Flex>
      </Flex>

      {isMobile ? (
        <MobileRightPanelDrawer
          isOpen={panel.isRightPanelOpen}
          onClose={panel.closePanel}
          isObjectOpen={panel.isObjectOpen}
          isObjectLoading={objectPending}
          isResultsLoading={resultsPending}
          currentObject={panel.currentObject}
          onSelectObject={handleSelectObject}
          searchValue={filters.search}
          searchInSelectedDefinitions={searchInSelectedDefinitions}
          onSearchInSelectedDefinitionsChange={
            handleSearchInSelectedDefinitionsChange
          }
          onSearch={handleSearch}
          onApplySearch={handleApplySearch}
          searchResults={panel.mapClickResults ?? matchedObjects}
          isClickResult={panel.mapClickResults?.length > 0}
          definitionValue={selectedDefinitionUuids}
          definitionsOptions={definitionsOptions}
          onDefinitionChange={handleDefinitionChange}
          isDefinitionRequired={isDefinitionRequired}
          onCancelMapClick={handleCancelMapClick}
          isLoading={isDataLoading}
          onMinimizedChange={setIsMobilePanelMinimized}
        />
      ) : (
        <RightPanelDrawer
          isOpen={panel.isRightPanelOpen}
          onClose={panel.closePanel}
          minimized={panel.isPanelMinimized}
          onToggleMinimize={() => panel.setIsPanelMinimized((prev) => !prev)}
          isSearchOpen={panel.isSearchOpen}
          isObjectOpen={panel.isObjectOpen}
          setIsSearchOpen={panel.setIsSearchOpen}
          setIsObjectOpen={panel.setIsObjectOpen}
          isObjectLoading={objectPending}
          isResultsLoading={resultsPending}
          currentObject={panel.currentObject}
          onSelectObject={handleSelectObject}
          searchValue={filters.search}
          searchInSelectedDefinitions={searchInSelectedDefinitions}
          onSearchInSelectedDefinitionsChange={
            handleSearchInSelectedDefinitionsChange
          }
          onSearch={handleSearch}
          onApplySearch={handleApplySearch}
          searchResults={panel.mapClickResults ?? matchedObjects}
          isClickResult={panel.mapClickResults?.length > 0}
          definitionValue={selectedDefinitionUuids}
          definitionsOptions={definitionsOptions}
          onDefinitionChange={handleDefinitionChange}
          isDefinitionRequired={isDefinitionRequired}
          onCancelMapClick={handleCancelMapClick}
          isLoading={isDataLoading}
        />
      )}
    </>
  );
};

export default MapView;
